import React, { useState, useEffect } from 'react';
import { apiFetch } from '../context/AuthContext';
import Evaluator from '../components/Evaluator';
import { ColorBadge, ScoreBars } from '../components/ProfileDisplay';

export default function TeamPage() {
  const [teams, setTeams]       = useState([]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(null);

  // Load teams the user belongs to
  useEffect(() => {
    apiFetch('/teams/mine')
      .then(data => {
        setTeams(data);
        if (data.length === 1) selectTeam(data[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function selectTeam(team) {
    setActiveTeam(team);
    setMembers([]);
    setExpanded(null);
    setLoadingMembers(true);
    apiFetch(`/teams/${team.id}/members`)
      .then(setMembers)
      .catch(() => {})
      .finally(() => setLoadingMembers(false));
  }

  function reload() {
    if (activeTeam) {
      setLoadingMembers(true);
      apiFetch(`/teams/${activeTeam.id}/members`)
        .then(setMembers).catch(() => {})
        .finally(() => setLoadingMembers(false));
    }
  }

  if (selected) {
    return <Evaluator
      subjectId={selected.id}
      subjectName={selected.name}
      onDone={() => { setSelected(null); reload(); }}
    />;
  }

  return (
    <div className="page-wide">
      <h1 style={{ fontFamily: 'Georgia,serif', fontSize: '1.8rem', fontWeight: 600, marginBottom: 4 }}>Team-Profile</h1>
      <p style={{ color: '#555', marginBottom: 28 }}>Eigene Profile und gegenseitige Einschätzungen deiner Teammitglieder.</p>

      {loading && <p style={{ color: '#555' }}>Laden…</p>}

      {/* No teams assigned */}
      {!loading && teams.length === 0 && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 48, color: '#555' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>👥</div>
            <p>Du bist noch keinem Team zugeordnet.</p>
            <p style={{ fontSize: '0.88rem', marginTop: 6 }}>Bitte wende dich an deinen Administrator.</p>
          </div>
        </div>
      )}

      {/* Team selector (if member of multiple teams) */}
      {teams.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
          {teams.map(t => (
            <button key={t.id} onClick={() => selectTeam(t)}
              style={{ padding: '9px 20px', borderRadius: 8, border: '2px solid', fontWeight: 600, fontSize: '0.92rem', cursor: 'pointer', transition: 'all 0.15s',
                background: activeTeam?.id === t.id ? '#1C1C1C' : '#fff',
                borderColor: activeTeam?.id === t.id ? '#1C1C1C' : '#AAAAAA',
                color: activeTeam?.id === t.id ? '#fff' : '#111' }}>
              {t.name}
              <span style={{ marginLeft: 8, fontSize: '0.75rem', opacity: 0.6 }}>{t.member_count} Mitgl.</span>
            </button>
          ))}
        </div>
      )}

      {/* Members list */}
      {activeTeam && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: 600, color: '#111' }}>{activeTeam.name}</h2>
            {activeTeam.description && <span style={{ fontSize: '0.85rem', color: '#777' }}>{activeTeam.description}</span>}
            <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#AAA' }}>{members.length} Mitglieder</span>
          </div>

          {loadingMembers && <p style={{ color: '#555', fontSize: '0.9rem' }}>Mitglieder laden…</p>}

          {!loadingMembers && members.length === 0 && (
            <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 32, color: '#888' }}>Keine weiteren Mitglieder in diesem Team.</div></div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {members.map(member => {
              const isExpanded = expanded === member.id;
              const hasProfile = !!member.dominant_color;
              const peers = member.peer_assessments || [];

              return (
                <div key={member.id} className="card" style={{ marginBottom: 0 }}>
                  <div style={{ padding: '16px 22px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    {/* Name */}
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111', marginBottom: 2 }}>{member.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#777' }}>{member.email}</div>
                    </div>

                    {/* Own profile */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777', marginBottom: 4 }}>Eigenes Profil</div>
                      {hasProfile ? <ColorBadge color={member.dominant_color} size="sm" /> : <span style={{ fontSize: '0.78rem', color: '#AAA' }}>Kein Profil</span>}
                    </div>

                    {/* Peer count */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777', marginBottom: 4 }}>Fremdeinschätzungen</div>
                      <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#333' }}>{member.peer_count || 0}</span>
                    </div>

                    {/* My assessment */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#777', marginBottom: 4 }}>Meine Einschätzung</div>
                      {member.my_assessment_color
                        ? <ColorBadge color={member.my_assessment_color} size="sm" />
                        : <button className="btn btn-primary btn-sm" onClick={() => setSelected(member)}>Einschätzen</button>
                      }
                    </div>

                    <button className="btn btn-outline btn-sm" onClick={() => setExpanded(isExpanded ? null : member.id)}>
                      {isExpanded ? 'Zuklappen ↑' : 'Details ↓'}
                    </button>
                  </div>

                  {isExpanded && (
                    <div style={{ borderTop: '2px solid #CCCCCC', padding: '20px 22px', background: '#FAFAF8' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#555', marginBottom: 10 }}>Selbstprofil</div>
                          {hasProfile
                            ? <ScoreBars avgR={member.avg_r} avgY={member.avg_y} avgG={member.avg_g} avgB={member.avg_b} />
                            : <p style={{ color: '#AAA', fontSize: '0.88rem' }}>{member.name} hat noch kein eigenes Profil ausgefüllt.</p>}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#555', marginBottom: 10 }}>
                            Fremdeinschätzungen ({peers.length})
                          </div>
                          {peers.length === 0
                            ? <p style={{ color: '#AAA', fontSize: '0.88rem' }}>Noch keine Fremdeinschätzungen.</p>
                            : peers.map((pa, i) => (
                              <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: i < peers.length - 1 ? '1px solid #DDD' : 'none' }}>
                                <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: 5, fontWeight: 600 }}>Von: {pa.assessor_name}</div>
                                <ColorBadge color={pa.dominant_color} size="sm" />
                                <ScoreBars avgR={pa.avg_r} avgY={pa.avg_y} avgG={pa.avg_g} avgB={pa.avg_b} />
                              </div>
                            ))
                          }
                        </div>
                      </div>
                      {member.my_assessment_color && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #DDD' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setSelected(member)}>Meine Einschätzung wiederholen</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
